import { showApiErrorToast } from './showApiErrorToast';

/** Shape consumed by `showApiErrorToast` for HTTP 200 business failures. */
function businessFailureError(messages: string[]): {
  body: { error: { message?: string; validationErrors?: Array<{ message: string }> } };
} {
  if (messages.length === 0) {
    return { body: { error: { message: 'Validation failed.' } } };
  }
  if (messages.length === 1) {
    return { body: { error: { message: messages[0] } } };
  }
  return {
    body: {
      error: {
        validationErrors: messages.map((message) => ({ message })),
      },
    },
  };
}

export interface SafeApiCallOptions<T> {
  /**
   * HTTP 200 business failure (e.g. `{ isSuccess: false, errors: string[] }`).
   * When true, messages are toasted and `undefined` is returned (same as a thrown API error).
   */
  rejectResponse?: (data: T) => boolean;
  /** Lines to toast when `rejectResponse` returns true. Defaults to a generic validation message. */
  rejectResponseMessages?: (data: T) => string[];
}

/**
 * Wraps a direct API call with automatic error toasting.
 *
 * Use in action classes (plain TypeScript, no React hooks) to eliminate
 * bare try/catch blocks. On error, `showApiErrorToast` is called and
 * `undefined` is returned — the error is never re-thrown.
 *
 * @example
 *   const data = await safeApiCall(() => ParticipantService.getById(id));
 *   if (data == null) return; // error already toasted
 *
 *   // HTTP 200 business failure (GrantImportResponseDto)
 *   const saved = await safeApiCall(() => GrantService.post...(payload), undefined, {
 *     rejectResponse: (r) => r.isSuccess === false,
 *     rejectResponseMessages: (r) => r.errors ?? [],
 *   });
 *   if (saved == null) return { success: false };
 */
export async function safeApiCall<T>(
  fn: () => Promise<T>,
  onError?: () => void,
  options?: SafeApiCallOptions<T>,
): Promise<T | undefined> {
  try {
    const data = await fn();

    if (options?.rejectResponse?.(data)) {
      const messages = (options.rejectResponseMessages?.(data) ?? []).filter(
        (line) => typeof line === 'string' && line.trim().length > 0,
      );
      throw businessFailureError(messages);
    }

    return data;
  } catch (error) {
    onError?.();
    showApiErrorToast(error);
    return undefined;
  }
}
